import {
  IGenericType,
  ISupplierCompanyProfile,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import React, {useState} from 'react';
import {apiUploadFile} from 'constants/Api';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Skeleton} from '@material-ui/lab';

const provinces = [
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Northern Cape',
  'North West',
  'Western Cape',
];

// numbers only, min 6 chars, max 20 chars
const accountNumberRegex = /^[0-9]{6,20}$/;

// numbers only, min 4 chars, max 20 chars
const branchCodeRegex = /^[0-9]{4,20}$/;

export function CompanyForm(props: {
  user: ISupplierUserProfile;
  company?: ISupplierCompanyProfile;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (company: ISupplierCompanyProfile) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
  editMargin?: boolean;
  isLoadingBankInfo?: boolean;
  bankTypes: IGenericType[];
  bankAccountTypes: IGenericType[];
  /** Whether this company acts as a Commodity Group (true) or a Supplier (false) */
  isCommodityGroup?: boolean;
  /** Called immediately when the user changes the Company Type toggle */
  onCompanyTypeChange?: (isCommodityGroup: boolean) => void;
}) {
  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control} = useForm<ISupplierCompanyProfile>({
    defaultValues: {
      ...props?.company,
      ...(props?.company?.depotPriceList === undefined && {
        depotPriceList: true,
      }),
    },
  });

  const [isUploadingProfileImage, setIsUploadingProfileImage] = useState(false);
  const [isProfileImageDragActive, setIsProfileImageDragActive] =
    useState(false);

  const getImageFromFileList = (files?: FileList | null) => {
    if (!files?.length) return undefined;
    return Array.from(files).find((file) => file.type.startsWith('image/'));
  };

  const uploadImage = (image?: File) => {
    if (!image) return;

    const fileExtension = image.name.split('.').pop()?.substr(0, 4);
    const fileKey = `${new Date().getTime()}_${
      props.user.userId
    }_profile.${fileExtension}`;

    setIsUploadingProfileImage(true);
    return apiUploadFile(image, fileKey)
      .catch((error) => {
        alert('Error uploading image. Please try again later');
        console.error(error);
        throw error;
      })
      .finally(() => setIsUploadingProfileImage(false));
  };

  const handleImageInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ) => {
    uploadImage(getImageFromFileList(event.target.files))?.then(onChange);
    event.target.value = '';
  };

  const handleImageDrop = (
    event: React.DragEvent<HTMLElement>,
    onChange: (value: string) => void,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setIsProfileImageDragActive(false);

    if (props.readonly || isUploadingProfileImage) return;

    const image = getImageFromFileList(event.dataTransfer.files);
    if (!image && event.dataTransfer.files.length) {
      alert('Please drop an image file');
      return;
    }

    uploadImage(image)?.then(onChange);
  };

  const onSubmit = (submission: ISupplierCompanyProfile) => {
    props.onSubmit({
      ...submission,
      freeDeliveryOrderTotal:
        parseFloat(submission.freeDeliveryOrderTotal as any) || undefined,
      marginPercentage:
        parseFloat(submission.marginPercentage as any) || undefined,
    });
  };

  const bankIds = props.bankTypes
    .sort((a, b) => a.name!.localeCompare(b.name!))
    .map((option) => option.id);

  const findBankName = (bankId: string) => {
    return props.bankTypes.find((option) => option.id === bankId)?.name;
  };

  const bankAccountTypeIds = props.bankAccountTypes
    .sort((a, b) => a.name!.localeCompare(b.name!))
    .map((option) => option.id);

  const findBankAccountTypeName = (bankAccountTypeId: string) => {
    return props.bankAccountTypes.find(
      (option) => option.id === bankAccountTypeId,
    )?.name;
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form company-form" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="image"
            control={control}
            render={({field: {onChange, value}}) => (
              <Box
                className={`photo-upload-container company-profile-upload drag-drop-upload form-section${
                  isProfileImageDragActive ? ' is-dragging' : ''
                }`}
                onDragEnter={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!props.readonly && !isUploadingProfileImage) {
                    setIsProfileImageDragActive(true);
                  }
                }}
                onDragOver={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onDragLeave={(event: React.DragEvent<HTMLElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsProfileImageDragActive(false);
                }}
                onDrop={(event: React.DragEvent<HTMLElement>) =>
                  handleImageDrop(event, onChange)
                }
              >
                <IconButton
                  component="label"
                  className="avatar company-profile-avatar-button"
                  size="medium"
                  disabled={props.readonly || isUploadingProfileImage}
                >
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      handleImageInputChange(event, onChange);
                    }}
                  />
                  <div className="avatar-container">
                    {isUploadingProfileImage ? (
                      <CircularProgress
                        color={'primary'}
                        size={20}
                        thickness={6}
                        className={'avatar'}
                      />
                    ) : (
                      <Avatar src={value} className="avatar avatar-border" />
                    )}
                  </div>
                </IconButton>
                <Box className="company-profile-upload-copy">
                  <Typography className="company-profile-upload-title">
                    Company profile
                  </Typography>
                  <Typography className="company-profile-upload-subtitle">
                    Drop an image here, or use the upload button.
                  </Typography>
                  {!props.readonly && (
                    <Button
                      className="company-upload-button"
                      component="label"
                      disabled={props.readonly || isUploadingProfileImage}
                      size="small"
                      variant="outlined"
                    >
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(event) => {
                          handleImageInputChange(event, onChange);
                        }}
                      />
                      Upload image
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          />
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Contact Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    autoFocus
                    className="input"
                    label="Company Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Company name required',
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="addressLine1"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Address Line 1"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Address line 1 required',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="addressLine2"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Address Line 2"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Address line 2 required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="city"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="City"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'City required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="province"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <Autocomplete
                    id="provinces"
                    disableClearable
                    options={provinces}
                    getOptionLabel={(province: string) => province}
                    size="small"
                    value={value}
                    disabled={props.readonly || props.isLoading}
                    onChange={(event, depot) => onChange(depot)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="input"
                        label="Province"
                        variant="outlined"
                        fullWidth
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={props.readonly || props.isLoading}
                        size="medium"
                      />
                    )}
                  />
                )}
                rules={{
                  required: 'Province required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="areaCode"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Area Code"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Area code required',
                }}
              />
            </Grid>
          </Grid>
          {/* ── Company Type ── */}
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Company Type
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                className="company-pricing-control"
              >
                <FormLabel component="legend">
                  Select whether this company is a Supplier or a Commodity
                  Group. This determines the permissions and pricing behaviour
                  for this company&apos;s users.
                </FormLabel>
                <RadioGroup
                  className="company-pricing-options"
                  value={
                    props.isCommodityGroup === true
                      ? 'commodityGroup'
                      : 'supplier'
                  }
                  onChange={(_, newValue) => {
                    if (!props.readonly && props.onCompanyTypeChange) {
                      props.onCompanyTypeChange(newValue === 'commodityGroup');
                    }
                  }}
                >
                  <FormControlLabel
                    className="company-pricing-option"
                    value="supplier"
                    control={<Radio color="primary" />}
                    disabled={props.readonly || props.isLoading}
                    label={
                      <Box>
                        <Typography variant="body2" style={{fontWeight: 600}}>
                          Supplier
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Full quote pricing with margins, cost prices and
                          discount fields.
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    className="company-pricing-option"
                    value="commodityGroup"
                    control={<Radio color="primary" />}
                    disabled={props.readonly || props.isLoading}
                    label={
                      <Box>
                        <Typography variant="body2" style={{fontWeight: 600}}>
                          Commodity Group
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Simplified pricing using product list price only.
                          Discount and cost-price fields are hidden.
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Pricing
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="depotPriceList"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl
                    className="company-pricing-control"
                    component="fieldset"
                    error={!!error}
                  >
                    <FormLabel component="legend">Pricing Type</FormLabel>
                    <RadioGroup
                      className="company-pricing-options"
                      value={value}
                      onChange={(event, newValue) =>
                        onChange(newValue === 'true')
                      }
                    >
                      <FormControlLabel
                        className="company-pricing-option"
                        value={true}
                        control={<Radio color={'primary'} />}
                        label={
                          <Typography variant={'body2'} color={'textSecondary'}>
                            Depot-specific pricing
                          </Typography>
                        }
                      />
                      <FormControlLabel
                        className="company-pricing-option"
                        value={false}
                        control={<Radio color={'primary'} />}
                        label={
                          <Typography variant={'body2'} color={'textSecondary'}>
                            Single price list
                          </Typography>
                        }
                      />
                    </RadioGroup>
                    <FormHelperText>{error?.message || null}</FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="marginPercentage"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Margin (%) (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={!props.editMargin}
                  />
                )}
                rules={{
                  validate: (value) => !isNaN(value!) || value === undefined,
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Logistics
              </Typography>
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="freeDeliveryOrderTotal"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Free Delivery Order Total (R) (Optional)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  validate: (value) => !isNaN(value!) || value === undefined,
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography
                className="company-form-section-title"
                variant={'subtitle2'}
                gutterBottom
              >
                Bank
              </Typography>
            </Grid>
            {props.isLoadingBankInfo && (
              <Grid item sm={12}>
                <Skeleton variant="rect" height={250} />
              </Grid>
            )}
            {!props.isLoadingBankInfo && (
              <>
                <Grid item xs={props.smallWidth ? 12 : 6}>
                  <Controller
                    name="bankId"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <Autocomplete
                        id="bankId"
                        disableClearable
                        options={bankIds}
                        getOptionLabel={(bankId) => findBankName(bankId) || ''}
                        size="small"
                        value={value}
                        disabled={
                          props.readonly ||
                          props.isLoading ||
                          !!props.company?.bankId
                        }
                        onChange={(event, depot) => onChange(depot)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="input"
                            label="Bank"
                            variant="outlined"
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : null}
                            disabled={props.readonly || props.isLoading}
                            size="medium"
                          />
                        )}
                      />
                    )}
                    rules={{
                      required: 'Bank required',
                    }}
                  />
                </Grid>
                <Grid item xs={props.smallWidth ? 12 : 6}>
                  <Controller
                    name="bankAccountTypeId"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <Autocomplete
                        id="bankAccountTypeId"
                        disableClearable
                        options={bankAccountTypeIds}
                        getOptionLabel={(bankAccountTypeId) =>
                          findBankAccountTypeName(bankAccountTypeId) || ''
                        }
                        size="small"
                        value={value}
                        disabled={
                          props.readonly ||
                          props.isLoading ||
                          !!props.company?.bankAccountTypeId
                        }
                        onChange={(event, depot) => onChange(depot)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="input"
                            label="Account Type"
                            variant="outlined"
                            fullWidth
                            error={!!error}
                            helperText={error ? error.message : null}
                            disabled={props.readonly || props.isLoading}
                            size="medium"
                          />
                        )}
                      />
                    )}
                    rules={{
                      required: 'Account type required',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="bankAccountNumber"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <TextField
                        className="input"
                        label="Account Number"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={
                          props.readonly ||
                          props.isLoading ||
                          !!props.company?.bankAccountNumber
                        }
                      />
                    )}
                    rules={{
                      required: 'Account number required',
                      validate: (value) =>
                        accountNumberRegex.test(value!) ||
                        'Account number should be valid and only contain numbers',
                    }}
                  />
                </Grid>
                <Grid item xs={props.smallWidth ? 12 : 6}>
                  <Controller
                    name="bankBranchCode"
                    control={control}
                    render={({
                      field: {onChange, value},
                      fieldState: {error},
                    }) => (
                      <TextField
                        className="input"
                        label="Branch Code"
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={value}
                        onChange={onChange}
                        error={!!error}
                        helperText={error ? error.message : null}
                        disabled={
                          props.readonly ||
                          props.isLoading ||
                          !!props.company?.bankBranchCode
                        }
                      />
                    )}
                    rules={{
                      required: 'Branch code required',
                      validate: (value) =>
                        branchCodeRegex.test(value!) ||
                        'Branch code should be valid and only contain numbers',
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} className="company-form-actions">
              <Grid item xs={props.smallWidth ? 12 : 6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading || isUploadingProfileImage}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>

              <Grid item xs={props.smallWidth ? 12 : 6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading || isUploadingProfileImage}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
